/**
 * Serah terima antar tahap — SATU-SATUNYA pintu masuk upah borongan.
 *
 * ⛔ KENAPA LAYAR INI YANG PALING MAHAL DARI SELURUH ANTREAN.
 *
 * Mesin upah borongan berdiri sejak 12 Agustus 2026 dan matang: tarif berversi,
 * pembekuan per FA, `amount_idr` dihitung DATABASE lewat `GENERATED ALWAYS`,
 * FK gabungan yang menolak tarif di luar yang beku.
 *
 * Diukur 31 Agustus 2026: `stage_handovers` **0 baris**. Nol pernah dipakai
 * satu potong pun — bukan karena rumusnya salah, melainkan karena NOL ADA
 * LAYAR yang bisa memasukkan datanya. Dua puluh hari mesin yang sudah dibayar
 * dan nol pernah bisa dipakai.
 *
 * ---------------------------------------------------------------------------
 * ⛔ TIGA HAL YANG LAYAR INI WAJIB TUNJUKKAN
 * ---------------------------------------------------------------------------
 *
 *  1. **Sisa yang DIHITUNG SISTEM, bukan diketik.** Angka acuannya datang dari
 *     `GET /serah-terima/sisa`. Membiarkan penerima mengetiknya berarti dua
 *     angka yang berbeda: yang ia kira dan yang service tegakkan.
 *
 *  2. **Selisih, seketika.** "Diharapkan 100, diterima 80" terbaca wajar
 *     sampai seseorang menghitung sendiri bahwa 20 potong menggantung.
 *
 *  3. **Rincian upah HANYA untuk JAHIT.** Trigger database menolak rincian
 *     dari tahap lain (K66/Q19a). Menampilkan isiannya di tahap lain berarti
 *     mengundang orang mengisi sesuatu yang pasti ditolak.
 *
 * VONIS-24SEP-BORONGAN (Owner 24 Sep 2026): di Jahit admin hanya mencatat POTONG per
 * baris item FA; komponen upah + nominal disusun sistem dari tarif yang dikunci saat FA
 * dibuat; group dipilih dari yang DITUGASKAN ke FA; FA bertarif belum lengkap ditolak.
 */
import React, { useCallback, useEffect, useState } from "react";
import { TAHAP_TANPA_SERAH_TERIMA } from "../lib/productionStages";
import { api } from "@/lib/api";
import { apiErrorMessage } from "@/lib/inventoryApi";
import { toast } from "sonner";
import { formatDateTime } from "@/lib/format";
import PilihFA from "@/components/etams/PilihFA";
import UpahBoronganFa from "@/components/etams/UpahBoronganFa";
import { DaftarKoreksiSerahTerima, FormAjukanKoreksi } from "@/components/etams/KoreksiSerahTerima";
import { useAuth } from "@/contexts/AuthContext";
import Hint from "@/components/etams/Hint";
import {
  barisDiBawahSisa, kolomJenis, labelBarisItem, labelBatal, labelPencatat, periksaIsian, potongSesuaiSisa, sisaBaris,
  tahapBerikutnya, teksJumlah, teksJumlahBaris, teksSisa, teksTahapOtomatis,
} from "@/lib/serahTerimaJenis";

// ⛔ `GUDANG_JADI` DITAMBAHKAN 2 September 2026 sebagai mata rantai TERAKHIR.
//    Sebelum itu daftar ini berhenti di QC_PACKING, dan karena tahap ASAL
//    dipilih dari `URUTAN.slice(0, -1)`, QC_PACKING nol pernah bisa jadi asal:
//    barang yang lolos QC nol punya tujuan yang sah, dan "pesanan tuntas" nol
//    pernah bisa dicatat sama sekali.
//
// ⚠ GUDANG_JADI adalah TUJUAN, bukan pekerjaan — ia nol punya tarif borongan
//   dan nol pernah jadi tahap ASAL (`slice(0, -1)` yang menjaminnya).
const URUTAN = ["LAYOUT", "PRINT_SUBLIM", "POTONG", "JAHIT", "FINISHING",
                "QC_PACKING", "GUDANG_JADI"];
const LABEL = {
  LAYOUT: "Layout", PRINT_SUBLIM: "Print / Sublim", POTONG: "Potong",
  JAHIT: "Jahit", FINISHING: "Finishing", QC_PACKING: "QC / Packing",
  GUDANG_JADI: "Gudang Barang Jadi",
};

export default function BoronganSerahTerima() {
  const [riwayat, setRiwayat] = useState([]);
  // BUSINESS-49: Admin Produksi mengajukan koreksi jumlah; KC region/Owner memutus di panel koreksi.
  const { roleCode } = useAuth();
  const bolehAjukanKoreksi = roleCode === "ADMIN_PRODUKSI";
  const [koreksiId, setKoreksiId] = useState(null);
  const [versiKoreksi, setVersiKoreksi] = useState(0);
  const [galat, setGalat] = useState("");
  const [muat, setMuat] = useState(true);

  // ⛔ SEBELUM 2 Sep 2026 keduanya kotak teks UUID kosong — pintu yang
  //    terkunci dari dalam (lihat PilihFA.jsx). Kini pilihan FA memberi
  //    keduanya sekaligus: FA dipilih lewat nomor, WO diturunkan darinya.
  const [pilihan, setPilihan] = useState(null);   // { fa, wo } | null
  const woId = pilihan?.wo?.id || "";
  const faId = pilihan?.fa?.id || "";
  const [dari, setDari] = useState("POTONG");
  // T74 (Owner 27 Okt 2026 "dibedakan fieldnya"): atasan & celana diisi terpisah di SEMUA tahap; total = keduanya.
  const [qtyA, setQtyA] = useState("");
  const [qtyC, setQtyC] = useState("");
  const qty = (Number(qtyA) || 0) + (Number(qtyC) || 0);
  // K229: cadangan Admin Produksi — "diinput admin atas nama" ketua tahap penerima (bila Telegram ketua bermasalah).
  const [ketuaList, setKetuaList] = useState([]);
  const [atasNama, setAtasNama] = useState("");
  const [groupId, setGroupId] = useState("");
  const [catatan, setCatatan] = useState("");
  const [sisa, setSisa] = useState(null);
  // VONIS-24SEP-BORONGAN: rencana upah FA (komponen dari FA, tarif beku saat FA dibuat) + group tertugas.
  const [upah, setUpah] = useState(null);
  const [pratinjau, setPratinjau] = useState(null);

  // ⛔ QC checklist S3 — muncul HANYA saat tahapnya QC & Packing. Daftar
  //    butirnya DIAMBIL DARI BACKEND (`/qc/meta`), nol ditulis di sini: dua
  //    daftar untuk satu checklist akan berselisih, dan butir yang hilang
  //    dari salah satunya adalah butir yang nol pernah diperiksa.
  const [qcMeta, setQcMeta] = useState(null);
  const [qcButir, setQcButir] = useState({});
  const [qcCatatan, setQcCatatan] = useState("");
  const [qcStatus, setQcStatus] = useState(null);
  const [qcSibuk, setQcSibuk] = useState(false);
  // Potong per baris item FA: { [fa_item_id]: { a, c } } — jenis pekerjaan TIDAK dipilih manual lagi.
  const [potong, setPotong] = useState({});
  const [groups, setGroups] = useState([]);
  const [sibuk, setSibuk] = useState(false);

  // ⛔ Tahap TUJUAN diturunkan dari tahap ASAL, nol diketik. Urutan tahap
  //    ditegakkan trigger database; membiarkan orang memilih tujuan bebas
  //    berarti mengundang penolakan yang sebabnya nol terlihat di layar.
  const ke = URUTAN[URUTAN.indexOf(dari) + 1] || null;
  const perluRincian = dari === "JAHIT";
  const perluQc = qcMeta && dari === qcMeta.tahap;

  useEffect(() => {
    api.get("/qc/meta").then(({ data }) => setQcMeta(data)).catch((e) => { setQcMeta(null); toast.error(apiErrorMessage(e, "Data gagal dimuat.")); });
  }, []);

  // Vonis gerbang untuk FA yang sedang diisi — dibaca ULANG tiap FA/tahap
  // berganti, supaya yang tampil nol pernah vonis FA sebelumnya.
  useEffect(() => {
    if (!perluQc || !faId) { setQcStatus(null); return; }
    api.get(`/qc/fa/${faId}`)
      .then(({ data }) => setQcStatus(data))
      .catch((e) => { setQcStatus(null); toast.error(apiErrorMessage(e, "Data gagal dimuat.")); });
  }, [perluQc, faId]);

  // T39: daftar butir QC yang berlaku untuk FA ini (lima butir + pasangan set bila pesanan set).
  const butirQc = qcMeta
    ? [...qcMeta.butir, ...(qcStatus?.pesanan_set && qcMeta.butir_set ? [qcMeta.butir_set] : [])]
    : [];
  const qcSemuaDicentang = butirQc.length > 0 && butirQc.every((b) => !!qcButir[b]);

  const catatQc = useCallback(async () => {
    if (!qcMeta || !faId) return null;
    setQcSibuk(true); setGalat("");
    try {
      await api.post("/qc", {
        fa_id: faId, catatan: qcCatatan || null,
        // Butir yang nol dicentang dikirim FALSE, bukan dilewati — backend
        // menolak butir yang hilang, dan "belum dijawab" memang bukan "tidak".
        ...Object.fromEntries(qcMeta.butir.map((b) => [b, !!qcButir[b]])),
        // K111 / BUSINESS-17: pasangan jersey–celana hanya dikirim untuk pesanan set.
        ...(qcStatus?.pesanan_set && qcMeta.butir_set ? { [qcMeta.butir_set]: !!qcButir[qcMeta.butir_set] } : {}),
      });
      const { data } = await api.get(`/qc/fa/${faId}`);
      setQcStatus(data);
      setQcCatatan("");
      return data;
    } catch (e) { setGalat(apiErrorMessage(e)); return null; }
    finally { setQcSibuk(false); }
  }, [qcMeta, faId, qcButir, qcCatatan, qcStatus]);

  const muatRiwayat = useCallback(async () => {
    setMuat(true);
    try {
      const { data } = await api.get("/borongan/riwayat");
      setRiwayat(data.items || []);
    } catch (e) {
      setGalat(apiErrorMessage(e));
    } finally {
      setMuat(false);
    }
  }, []);

  useEffect(() => { muatRiwayat(); }, [muatRiwayat]);

  useEffect(() => {
    // ⚠ ALAMATNYA DIPERIKSA KE ROUTER, BUKAN DITEBAK DARI NAMA TABEL.
    //   Versi pertama memanggil `/master/sewing-groups` — nol pernah ada.
    //   Yang benar `/group-jahit` (routers/group_jahit.py). Tebakan seperti
    //   itu jatuh sebagai 404 yang tertelan `.catch` di bawah, lalu dropdown
    //   group-nya KOSONG SELAMANYA tanpa satu pun galat terlihat.
    // (VONIS-24SEP-BORONGAN: daftar jenis pekerjaan tidak lagi dimuat — komponen upah disusun sistem.)
    api.get("/group-jahit")
      .then(({ data }) => setGroups(data.items || data || []))
      .catch((e) => { setGroups([]); setGalat(apiErrorMessage(e)); });
  }, []);

  // Rencana upah FA dimuat hanya untuk serah terima dari Jahit.
  const muatUpah = useCallback(() => {
    if (!faId || !perluRincian) return;
    api.get(`/borongan/fa/${faId}/upah`)
      .then(({ data }) => setUpah(data))
      .catch((e) => setGalat(apiErrorMessage(e)));
  }, [faId, perluRincian]);
  useEffect(() => {
    setUpah(null); setPotong({}); setPratinjau(null);
    muatUpah();
  }, [muatUpah]);
  // T97 (Owner 1 Nov 2026 "potong per baris item nya mengapa banyak field nya begitu ya ?"): kotak terisi OTOMATIS
  // sesuai sisa tiap baris; staf cukup mengubah baris yang kurang/sebagian.
  useEffect(() => {
    if (perluRincian && upah?.items) setPotong(potongSesuaiSisa(upah.items));
  }, [perluRincian, upah]);

  const daftarPotong = Object.entries(potong)
    .map(([fa_item_id, v]) => ({ fa_item_id, qty_atasan: Number(v.a) || 0, qty_celana: Number(v.c) || 0 }))
    .filter((p) => p.qty_atasan + p.qty_celana > 0);
  const totalPotong = daftarPotong.reduce((t, p) => t + p.qty_atasan + p.qty_celana, 0);
  const kunciPotong = JSON.stringify(daftarPotong);
  // Jumlah yang akan dicatat lebih kecil dari sisa menurut sistem → wajib alasan (Q20, tercatat "belum lengkap").
  const jumlahKirim = perluRincian ? totalPotong : qty;
  const kurang = !!sisa && jumlahKirim > 0 && jumlahKirim < Number(sisa.sisa ?? 0);
  const barisKurang = perluRincian && upah ? barisDiBawahSisa(upah.items, potong) : [];
  const groupTertugas = upah?.group_tertugas || [];
  const namaGroup = (id) => {
    const g = groups.find((x) => x.id === id);
    return g ? (g.name || g.code) : id;
  };

  // Pratinjau rincian komponen dari sistem (baca saja) tiap potong berubah.
  useEffect(() => {
    if (!faId || !perluRincian || kunciPotong === "[]" || upah?.tarif_lengkap === false) {
      setPratinjau(null);
      return undefined;
    }
    const t = setTimeout(() => {
      api.get("/borongan/serah-terima/pratinjau", { params: { fa_id: faId, potong: kunciPotong } })
        .then(({ data }) => setPratinjau(data))
        .catch((e) => { setPratinjau(null); setGalat(apiErrorMessage(e)); });
    }, 300);
    return () => clearTimeout(t);
  }, [faId, perluRincian, kunciPotong, upah?.tarif_lengkap]);

  // Sisa dihitung otomatis begitu FA + tahap asal lengkap — dulu menunggu
  // `onBlur` kotak UUID yang kini nol ada.
  useEffect(() => {
    if (faId && dari) cekSisa();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [faId, dari]);

  // T36: begitu FA dipilih, "Dari tahap" terisi otomatis = tahap pertama yang masih bersisa.
  useEffect(() => {
    if (!faId) return;
    const asal = URUTAN.slice(0, -1).filter((k) => !TAHAP_TANPA_SERAH_TERIMA.includes(k));
    Promise.all(asal.map((t) => api.get("/borongan/serah-terima/sisa", { params: { fa_id: faId, from_stage_code: t } })
      .then(({ data }) => ({ tahap: t, sisa: data }))
      .catch((e) => { toast.error(apiErrorMessage(e, "Sisa per tahap gagal dimuat.")); return { tahap: t, sisa: null }; })))
      .then((daftar) => { const t = tahapBerikutnya(daftar); if (t) setDari(t); });
  }, [faId]);

  // K229: ketua tahap penerima yang sah untuk pilihan "diinput atas nama".
  useEffect(() => {
    setAtasNama(""); setKetuaList([]);
    if (!faId || !ke) return;
    api.get("/borongan/serah-terima/ketua-penerima", { params: { fa_id: faId, to_stage_code: ke } })
      .then(({ data }) => setKetuaList(data.items || []))
      .catch((e) => { setKetuaList([]); toast.error(apiErrorMessage(e, "Daftar ketua tahap gagal dimuat.")); });
  }, [faId, ke]);

  const cekSisa = async () => {
    setGalat("");
    setSisa(null);
    // T36: tombol tidak lagi diam bila pesanan belum dipilih.
    if (!faId) { setGalat("Pilih pesanan dulu."); return; }
    if (!dari) return;
    try {
      const { data } = await api.get("/borongan/serah-terima/sisa", {
        params: { fa_id: faId, from_stage_code: dari },
      });
      setSisa(data);
    } catch (e) {
      setGalat(apiErrorMessage(e));
    }
  };

  const simpan = async () => {
    // Padanan layar untuk pagar service GROUP_WAJIB_JAHIT (CHECK DB
    // stage_handovers_group_only_for_jahit): ditolak SEBELUM memanggil API.
    if (perluRincian && !groupId) {
      setGalat("Serah terima dari Jahit wajib menyebut group jahit yang mengerjakannya — upah borongan dicatat atas nama group.");
      return;
    }
    // T39: keluar dari QC & Packing dengan semua butir sudah dicentang tetapi pemeriksaan belum dicatat →
    // pemeriksaannya dicatat SEKALIAN (dulu tetap "BELUM LOLOS" sampai tombol QC diklik terpisah).
    if (perluQc && faId && !qcStatus?.lolos) {
      if (!qcSemuaDicentang) {
        setGalat("Barang belum lolos QC. Centang semua butir pemeriksaan yang sudah sesuai, lalu catat serah terima lagi.");
        return;
      }
      const hasil = await catatQc();
      if (!hasil?.lolos) return;
    }
    if (!perluRincian) {
      const salah = periksaIsian(qtyA, qtyC, sisa);
      if (salah) { setGalat(salah); return; }
    }
    // Q20: jumlah di bawah sisa sah bila DINYATAKAN beserta alasannya (backend: izinkan_tertunda + alasan ≥ 10 huruf).
    if (kurang && catatan.trim().length < 10) {
      setGalat("Jumlah ini kurang dari sisa. Tulis alasannya di kolom Alasan kurang (minimal 10 huruf), misalnya \"6 celana masih dijahit\".");
      return;
    }
    setSibuk(true);
    setGalat("");
    try {
      const { data } = await api.post("/borongan/serah-terima", {
        wo_id: woId, fa_id: faId,
        from_stage_code: dari, to_stage_code: ke,
        // Jahit: jumlah diterima = Σ potong per baris item (satu peristiwa, satu angka).
        qty_received: perluRincian ? totalPotong : qty,
        // T74: atasan & celana terpisah (Jahit: diturunkan backend dari potong per baris item).
        ...(perluRincian ? {} : { qty_atasan: Number(qtyA) || 0, qty_celana: Number(qtyC) || 0 }),
        sewing_group_id: perluRincian ? (groupId || null) : null,
        potong: perluRincian ? daftarPotong : [],
        ...(kurang ? { izinkan_tertunda: true, alasan_tertunda: catatan.trim(), notes: null }
          : { notes: catatan || null }),
        atas_nama_employee_id: atasNama || null,
      });
      toast.success(`Serah terima ${LABEL[dari]} → ${LABEL[ke]} tercatat`);
      // K235 (BUSINESS-114): tahap WO yang ikut bergerak — staf tidak perlu menekan Mulai/Selesaikan lagi.
      const gerak = teksTahapOtomatis(data?.tahap_otomatis, LABEL);
      if (gerak) toast.info(gerak, { duration: 10000 });
      // T38: koreksi jumlah ke tahap ini yang masih menunggu keputusan — diberi tahu sekarang.
      if (data?.peringatan) toast.warning(data.peringatan, { duration: 15000 });
      // T36: FA tetap terpilih sesudah simpan; sisa dimuat ulang supaya angka berikutnya langsung terlihat.
      setQtyA(""); setQtyC(""); setPotong({}); setPratinjau(null); setCatatan(""); setAtasNama("");
      cekSisa();
      if (perluRincian) {
        api.get(`/borongan/fa/${faId}/upah`).then(({ data: u }) => setUpah(u)).catch((e) => toast.error(apiErrorMessage(e, "Data pendukung gagal dimuat.")));
      }
      await muatRiwayat();
      return data;
    } catch (e) {
      setGalat(apiErrorMessage(e));
    } finally {
      setSibuk(false);
    }
  };


  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold text-foreground">
          Serah Terima Tahap{" "}
          <Hint>Mencatat perpindahan pekerjaan antar tahap. Serah terima dari Jahit sekaligus mencatat upah borongan group-nya.</Hint>
        </h1>
      </div>

      {galat && (
        <div role="alert" className="rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {galat}
        </div>
      )}

      <div className="rounded-xl border border-border bg-card p-4 space-y-3">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
          <div className="block md:col-span-2">
            <span className="mb-1 block text-sm font-medium text-foreground">Pesanan (FA) &amp; WO</span>
            <PilihFA value={pilihan} disabled={sibuk}
                     onChange={(v) => { setPilihan(v); setSisa(null); }} />
          </div>
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-foreground">Dari tahap</span>
            <select className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground"
                    value={dari} onChange={(e) => { setDari(e.target.value); setSisa(null); }}>
              {/* ⛔ Mulai dari POTONG (urutan 3), bukan LAYOUT. Constraint DB Q22
                  menolak serah terima berkuantitas dari tahap 1–2 — dan sebelum
                  2 Sep 2026 layar ini tetap MENAWARKAN keduanya, lalu orang
                  membaca galat '23514' yang nol menjelaskan apa-apa. Yang nol
                  bisa dipilih jangan ditawarkan. */}
              {URUTAN.slice(0, -1).filter((k) => !TAHAP_TANPA_SERAH_TERIMA.includes(k)).map((k) => (
                <option key={k} value={k}>{LABEL[k]}</option>
              ))}
            </select>
          </label>
          <div>
            <span className="mb-1 block text-sm font-medium text-foreground">Ke tahap</span>
            {/* Diturunkan, nol diketik — lihat catatan di atas. */}
            <div className="rounded-lg border border-border bg-muted px-3 py-2 text-sm text-muted-foreground">
              {ke ? LABEL[ke] : "—"}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <button type="button" onClick={cekSisa}
                  className="rounded-lg border border-input px-4 py-2 text-sm font-medium text-foreground hover:bg-muted">
            Cek sisa
          </button>
          {/* ⛔ Butir 1 — angka acuan DIHITUNG SISTEM. */}
          {sisa && (
            <div className="rounded-lg border border-border bg-muted px-3 py-2 text-sm">
              <span className="text-muted-foreground">Sisa menurut sistem: </span>
              <span className="tabular-nums font-semibold text-foreground" data-testid="sisa-per-jenis">
                {teksSisa(sisa)}
              </span>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          {perluRincian ? (
            <div>
              <span className="mb-1 block text-sm font-medium text-foreground">Jumlah diterima</span>
              {/* Jahit: dihitung dari potong per baris item di bawah — bukan diketik. */}
              <div className="rounded-lg border border-border bg-muted px-3 py-2 text-sm tabular-nums text-foreground"
                   data-testid="qty-dari-potong">
                {teksJumlah(daftarPotong.reduce((t, p) => t + p.qty_atasan, 0),
                            daftarPotong.reduce((t, p) => t + p.qty_celana, 0))} (total {totalPotong})
              </div>
            </div>
          ) : (
            <div className="block" data-testid="isian-per-jenis">
              <span className="mb-1 block text-sm font-medium text-foreground">Jumlah diterima</span>
              <div className="flex flex-wrap items-center gap-2">
                {kolomJenis(sisa).atasan && (
                  <label className="flex items-center gap-1 text-sm">
                    <span className="text-muted-foreground">Atasan</span>
                    <input type="number" min="0" inputMode="numeric" data-testid="qty-atasan"
                           className="w-20 rounded-lg border border-input bg-background px-2 py-2 text-sm text-foreground"
                           value={qtyA} onChange={(e) => setQtyA(e.target.value)} />
                  </label>
                )}
                {kolomJenis(sisa).celana && (
                  <label className="flex items-center gap-1 text-sm">
                    <span className="text-muted-foreground">Celana</span>
                    <input type="number" min="0" inputMode="numeric" data-testid="qty-celana"
                           className="w-20 rounded-lg border border-input bg-background px-2 py-2 text-sm text-foreground"
                           value={qtyC} onChange={(e) => setQtyC(e.target.value)} />
                  </label>
                )}
                <span className="text-xs tabular-nums text-muted-foreground">total {qty}</span>
              </div>
            </div>
          )}
          {perluRincian && (
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-foreground">Group jahit (yang ditugaskan)</span>
              <select className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground"
                      value={groupId} onChange={(e) => setGroupId(e.target.value)} data-testid="pilih-group-jahit">
                <option value="">Pilih group…</option>
                {groupTertugas.map((g) => (
                  <option key={g.sewing_group_id} value={g.sewing_group_id}>
                    {g.name || g.code || namaGroup(g.sewing_group_id)}
                  </option>
                ))}
              </select>
              {upah && groupTertugas.length === 0 && (
                <span className="mt-1 block text-xs text-warning-soft-foreground">
                  Belum ada group jahit yang ditugaskan ke FA ini — tugaskan di kartu “Upah borongan jahit” di bawah.
                </span>
              )}
            </label>
          )}
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-foreground">
              {kurang ? "Alasan kurang (wajib)" : "Catatan"}
            </span>
            <input className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground"
                   data-testid="catatan-serah-terima"
                   placeholder={kurang ? "Mis. 6 celana masih dijahit" : ""}
                   value={catatan} onChange={(e) => setCatatan(e.target.value)} />
          </label>
          {/* K229: biasanya ketua tahap penerima mencatat sendiri lewat Telegram. Layar ini cadangan bila HP/Telegram
              ketua bermasalah — pilih nama ketuanya supaya tercatat "diinput admin atas nama …" dan pemberi dikabari. */}
          {faId && ke && (
            <label className="block md:col-span-3">
              <span className="mb-1 block text-sm font-medium text-foreground">
                Diinput atas nama ketua tahap penerima{" "}
                <Hint>Isi bila ketua tahap {LABEL[ke]} tidak bisa mencatat lewat Telegram. Kosongkan bila Anda mencatat sebagai diri sendiri.</Hint>
              </span>
              <select className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground"
                      value={atasNama} onChange={(e) => setAtasNama(e.target.value)} data-testid="pilih-atas-nama">
                <option value="">— tidak, dicatat atas nama saya —</option>
                {ketuaList.map((k) => (
                  <option key={k.employee_id} value={k.employee_id}>{k.nama} ({k.keterangan})</option>
                ))}
              </select>
              {ketuaList.length === 0 && (
                <span className="mt-1 block text-xs text-muted-foreground" data-testid="ketua-belum-tercatat">
                  {/* K230 (BUSINESS-108): ketua = KETUA tercatat di Personil per Tahap Kerja; tanpa ketua → Admin Produksi. */}
                  Belum ada ketua tahap {LABEL[ke]} di Personil per Tahap Kerja cabang ini — Admin Produksi mencatat
                  serah terima ini di layar ini atas nama sendiri.
                </span>
              )}
            </label>
          )}
        </div>

        {/* ⛔ Butir 2 — selisih seketika, bukan disimpulkan pembaca. */}
        {sisa && !perluRincian && qty > 0 && (
          <p className={
            "text-sm " +
            (qty < (sisa.sisa ?? 0)
              ? "text-warning-soft-foreground" : "text-muted-foreground")
          }>
            {qty < (sisa.sisa ?? 0)
              ? `Selisih ${(sisa.sisa ?? 0) - qty} potong — sisanya tetap menggantung di ${LABEL[dari]}.`
              : "Tidak ada selisih."}
          </p>
        )}

        {/* ⛔ Butir 3 — rincian upah HANYA untuk JAHIT. Trigger database
            menolak rincian dari tahap lain (K66/Q19a); menampilkan isiannya
            di tahap lain berarti mengundang penolakan yang sebabnya nol
            terlihat di layar. */}
        {/* VONIS-24SEP-BORONGAN: admin cukup mencatat POTONG per baris item FA; komponen upah
            (dasar, kerah, lengan panjang, raglan) dan nominalnya dihitung sistem dari tarif yang
            dikunci saat FA dibuat. Pilihan jenis pekerjaan manual dihapus. */}
        {perluRincian && upah && (
          <div className="rounded-lg border border-border p-3 space-y-3" data-testid="potong-per-baris">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm font-medium text-foreground">
                Potong per baris item
                <span className="ml-2 tabular-nums text-muted-foreground">{totalPotong} potong</span>
              </p>
              {/* T97: kotak terisi otomatis sesuai sisa; tombol ini mengembalikan semua baris ke sisanya. */}
              <button type="button" onClick={() => setPotong(potongSesuaiSisa(upah.items))}
                      className="rounded-lg border border-input px-3 py-1 text-xs font-medium text-foreground hover:bg-muted"
                      data-testid="potong-semua-sisa">
                Semua sesuai sisa
              </button>
            </div>
            <p className="text-xs text-muted-foreground">
              Sudah terisi sesuai sisa tiap baris. Ubah hanya baris yang jumlahnya kurang atau baru sebagian.
            </p>
            {barisKurang.length > 0 && (
              <p className="rounded border border-warning-soft-border bg-warning-soft p-2 text-xs text-warning-soft-foreground"
                 data-testid="potong-di-bawah-sisa">
                Di bawah sisa: {barisKurang.join("; ")}. Sisanya tetap menunggu di tahap Jahit.
              </p>
            )}
            {upah.tarif_lengkap === false && (
              <p role="alert" className="rounded border border-destructive/40 bg-destructive/10 p-2 text-sm text-destructive"
                 data-testid="tarif-belum-lengkap">
                Tarif borongan FA ini belum lengkap:{" "}
                {(upah.komponen_kurang || []).map((k) => k.label || k.jenis).join(", ")}. Serah terima Jahit
                ditolak sampai Owner/Kepala Cabang melengkapi tarif komponen.
              </p>
            )}
            <div className="space-y-2">
              {(upah.items || []).map((it) => (
                <div key={it.id} className="grid grid-cols-1 items-center gap-2 rounded-md bg-muted px-3 py-2 sm:grid-cols-3">
                  <span className="text-sm text-foreground" data-testid={`label-baris-${it.id}`}>
                    {labelBarisItem(it)}
                    <span className="ml-1 text-xs text-muted-foreground tabular-nums">
                      (sisa {sisaBaris(it).a} atasan{Number(it.qty_celana) > 0 ? ` · ${sisaBaris(it).c} celana` : ""}
                      {" "}dari {it.qty_atasan}{Number(it.qty_celana) > 0 ? `/${it.qty_celana}` : ""})
                    </span>
                  </span>
                  <label className="flex items-center gap-2 text-sm">
                    <span className="text-muted-foreground">Atasan</span>
                    <input type="number" min="0" max={Math.max(0, it.qty_atasan - it.sudah_atasan)}
                           className="w-24 rounded border border-input bg-background px-2 py-1 text-sm text-foreground"
                           value={potong[it.id]?.a || ""}
                           onChange={(e) => setPotong((p) => ({ ...p, [it.id]: { ...(p[it.id] || {}), a: e.target.value } }))} />
                  </label>
                  {Number(it.qty_celana) > 0 && (
                    <label className="flex items-center gap-2 text-sm">
                      <span className="text-muted-foreground">Celana</span>
                      <input type="number" min="0" max={Math.max(0, it.qty_celana - it.sudah_celana)}
                             className="w-24 rounded border border-input bg-background px-2 py-1 text-sm text-foreground"
                             value={potong[it.id]?.c || ""}
                             onChange={(e) => setPotong((p) => ({ ...p, [it.id]: { ...(p[it.id] || {}), c: e.target.value } }))} />
                    </label>
                  )}
                </div>
              ))}
            </div>
            {pratinjau && pratinjau.rincian?.length > 0 && (
              <div className="rounded-md border border-border p-2 text-sm" data-testid="pratinjau-rincian">
                <p className="mb-1 font-medium text-foreground">Rincian upah dari sistem</p>
                {pratinjau.rincian.map((r) => (
                  <div key={r.work_type_id} className="flex justify-between tabular-nums">
                    <span>{r.komponen} × {r.qty}</span>
                    {r.amount_idr !== undefined && <span>Rp{Number(r.amount_idr).toLocaleString("id-ID")}</span>}
                  </div>
                ))}
                {pratinjau.total_idr != null && (
                  <div className="mt-1 flex justify-between border-t border-border pt-1 font-medium tabular-nums">
                    <span>Total</span><span>Rp{Number(pratinjau.total_idr).toLocaleString("id-ID")}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* VONIS-24SEP-BORONGAN: Admin Produksi menugaskan group jahit per FA — ia tidak membuka
            halaman FA (FA_DETAIL_ROLES), jadi kartunya dipasang juga di sini. */}
        {perluRincian && faId && <UpahBoronganFa faId={faId} onBerubah={muatUpah} />}

        {/* ⛔ GERBANG S3 — muncul HANYA di QC & Packing. Di tahap lain ia
            nol ditampilkan sama sekali: menuntut lima centang untuk barang
            yang bahkan belum jadi memaksa orang mencentang tanpa memeriksa,
            dan centang yang diberikan tanpa memeriksa lebih buruk daripada
            nol centang. */}
        {perluQc && faId && (
          <div className="rounded-xl border border-border bg-card p-4 space-y-3"
               data-testid="qc-checklist">
            <div className="flex items-center justify-between">
              <p className="font-medium text-foreground">QC &amp; Packing — checklist wajib</p>
              {qcStatus && (
                <span className={qcStatus.lolos
                  ? "rounded-full border border-success-soft-border bg-success-soft px-2 py-0.5 text-xs text-success-soft-foreground"
                  : "rounded-full border border-danger-soft-border bg-danger-soft px-2 py-0.5 text-xs text-danger-soft-foreground"}>
                  {qcStatus.lolos ? "LOLOS" : "BELUM LOLOS"}
                </span>
              )}
            </div>

            {/* Vonis gerbang ditampilkan APA ADANYA dari backend — dua sebab
                menuntut tindakan berbeda, dan layar yang menulis kalimatnya
                sendiri akan berselisih dengan yang menolak. */}
            {qcStatus && !qcStatus.lolos && (
              <p className="rounded border border-warning-soft-border bg-warning-soft p-2 text-xs text-warning-soft-foreground">
                {qcStatus.pesan}
              </p>
            )}

            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {butirQc.map((b) => (
                <label key={b} className="flex items-center gap-2 text-sm text-foreground">
                  <input type="checkbox" checked={!!qcButir[b]}
                         onChange={(e) => setQcButir((v) => ({ ...v, [b]: e.target.checked }))}
                         data-testid={`qc-${b}`} />
                  {qcMeta.label[b]}
                </label>
              ))}
            </div>

            <input value={qcCatatan} onChange={(e) => setQcCatatan(e.target.value)}
                   placeholder="Catatan (opsional) — untuk hal di luar lima butir"
                   className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground" />

            <div className="flex items-center justify-between">
              {/* ⚠ Kalimat ini ada supaya orang TAHU pemeriksaan gagal tetap
                  dicatat — tanpa itu ia akan menunda mencatat sampai semuanya
                  hijau, dan barang yang ditahan jadi nol punya jejak. */}
              <p className="text-xs text-muted-foreground" data-testid="qc-petunjuk">
                {qcSemuaDicentang && !qcStatus?.lolos
                  ? "Semua butir sudah dicentang — pemeriksaan ikut tercatat saat Anda menekan \"Catat serah terima\"."
                  : "Hasil yang belum lolos tetap tercatat sebagai bukti barang diperiksa."}
              </p>
              <button type="button" onClick={catatQc} disabled={qcSibuk}
                      className="rounded-lg border border-border px-3 py-1.5 text-sm text-foreground hover:bg-muted disabled:opacity-50"
                      data-testid="qc-simpan">
                {qcSibuk ? "Menyimpan…" : "Catat pemeriksaan QC"}
              </button>
            </div>
          </div>
        )}

        <div className="flex justify-end">
          <button type="button" disabled={sibuk || !woId || !faId || !ke || (perluRincian ? (totalPotong === 0 || upah?.tarif_lengkap === false) : qty <= 0)}
                  onClick={simpan}
                  className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50">
            {sibuk ? "Menyimpan…" : "Catat serah terima"}
          </button>
        </div>
      </div>

      <DaftarKoreksiSerahTerima label={(c) => `${LABEL[c] || c}`} versi={versiKoreksi}
                                onBerubah={muatRiwayat} />

      <div>
        <h2 className="mb-2 text-lg font-semibold text-foreground">Riwayat</h2>
        {muat && <p className="text-sm text-muted-foreground">Memuat…</p>}
        {!muat && riwayat.length === 0 && (
          <div className="rounded-xl border border-border bg-card p-8 text-center text-sm text-muted-foreground">
            Belum ada serah terima tercatat.
          </div>
        )}
        <div className="space-y-2">
          {riwayat.map((h) => (
            <div key={h.id} className={`rounded-xl border border-border bg-card p-3 text-sm ${h.dibatalkan_pada ? "opacity-60" : ""}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium text-foreground">
                  {LABEL[h.from_stage_code] || h.from_stage_code} → {LABEL[h.to_stage_code] || h.to_stage_code}
                </span>
                <span className="text-muted-foreground">{h.fa_number}</span>
                {h.group_nama && (
                  <span className="rounded border border-border px-1.5 text-xs text-muted-foreground">
                    {h.group_nama}
                  </span>
                )}
                {/* ⚠ Status TERTUNDA terlihat persis sama dengan COMPLETE
                    kalau disembunyikan — dan bedanya pekerjaan yang masih
                    menggantung di tahap sebelumnya. */}
                {/* BUSINESS-49: catatan lama tidak dihapus — tampil dicoret, bertanda dibatalkan koreksi. */}
                {h.dibatalkan_pada && (
                  <span className="rounded border border-destructive/50 bg-destructive/10 px-1.5 text-xs text-destructive">
                    {labelBatal(h)}
                  </span>
                )}
                {labelPencatat(h) && (
                  <span className="rounded border border-border px-1.5 text-xs text-muted-foreground" data-testid={`pencatat-${h.id}`}>
                    {labelPencatat(h)}
                  </span>
                )}
                {h.koreksi_dari_handover_id && (
                  <span className="rounded border border-info-soft-border bg-info-soft px-1.5 text-xs text-info-soft-foreground">
                    HASIL KOREKSI
                  </span>
                )}
                {h.status !== "COMPLETE" && !h.dibatalkan_pada && (
                  <span className="rounded border border-warning-soft-border bg-warning-soft px-1.5 text-xs text-warning-soft-foreground">
                    Belum lengkap
                  </span>
                )}
              </div>
              <p className={`mt-0.5 tabular-nums text-muted-foreground ${h.dibatalkan_pada ? "line-through" : ""}`}>
                {teksJumlahBaris(h)} dari sisa {h.qty_expected_remaining}
                {h.selisih > 0 && (
                  <span className="ml-2 text-warning-soft-foreground">
                    selisih {h.selisih}
                  </span>
                )}
                <span className="ml-2">{formatDateTime(h.handover_at)}</span>
              </p>
              {h.dibatalkan_pada && h.alasan_batal && (
                <p className="mt-0.5 text-xs text-muted-foreground">{h.alasan_batal}</p>
              )}
              {/* K229: "Tidak sesuai" dari ketua tahap pemberi / "Ajukan koreksi" dari pengirim lewat Telegram. */}
              {(h.laporan_tg || []).map((l) => (
                <p key={l.jenis} className="mt-1 rounded border border-warning-soft-border bg-warning-soft px-2 py-1 text-xs text-warning-soft-foreground"
                   data-testid={`laporan-tg-${h.id}`}>
                  {l.jenis === "TIDAK_SESUAI" ? "Tahap pemberi melaporkan jumlahnya TIDAK SESUAI" : "Ketua tahap meminta koreksi"}
                  {l.keterangan ? `: ${l.keterangan}` : ""} ({formatDateTime(l.created_at)})
                </p>
              ))}
              {bolehAjukanKoreksi && !h.dibatalkan_pada && (
                koreksiId === h.id ? (
                  <FormAjukanKoreksi handover={h}
                                     label={`${LABEL[h.from_stage_code] || h.from_stage_code} → ${LABEL[h.to_stage_code] || h.to_stage_code}`}
                                     onBatal={() => setKoreksiId(null)}
                                     onSelesai={() => { setKoreksiId(null); setVersiKoreksi((v) => v + 1); }} />
                ) : (
                  <button type="button" onClick={() => setKoreksiId(h.id)}
                          className="mt-1 text-xs text-primary underline-offset-2 hover:underline">
                    Ajukan koreksi jumlah
                  </button>
                )
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
